/**
 * The floor spotlight's selection state — what a choice *is*, how it survives a URL round trip, and
 * which choices the result offers. No JSX: this is the rule, and `spotlight.tsx` is the control, the
 * same split `chips.ts` keeps from its toolbar.
 */

import type { SewingRow } from './columns';

/** One unambiguous Building-Floor choice in the floor spotlight. */
export interface BuildingFloor {
  building: string;
  floor: string;
}

/** A pair plus its URL value and its button label. */
export interface BuildingFloorOption extends BuildingFloor {
  value: string;
  label: string;
}

/** A URL-safe selector value for a building/floor pair. */
export function buildingFloorValue({ building, floor }: BuildingFloor): string {
  return JSON.stringify([building, floor]);
}

/** Parses a Building-Floor spotlight value without trusting a hand-edited URL. */
export function parseBuildingFloor(value: string | null): BuildingFloor | null {
  if (value === null) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (
      Array.isArray(parsed) &&
      parsed.length === 2 &&
      typeof parsed[0] === 'string' &&
      typeof parsed[1] === 'string'
    ) {
      return { building: parsed[0], floor: parsed[1] };
    }
  } catch {
    // A stale or hand-edited URL simply has no selected spotlight.
  }
  return null;
}

/**
 * The Building-Floor pairs that actually ran, read off the result rather than a static list.
 *
 * **The caller must pass the complete, unchipped summary result.** Complete, because "which floors
 * exist" derived from a capped result is a short list presented as the set (§4.3). Unchipped,
 * because a chip selects rows for the panels below and must not close a floor — a floor whose every
 * line ran sample production would otherwise vanish the moment someone asked for non-sample lines.
 *
 * Order comes from the rows, in the server's grain order (§4.6), so no client collation is invented
 * to disagree with ADB's.
 */
export function buildingFloorOptions(rows: readonly SewingRow[]): readonly BuildingFloorOption[] {
  const pairs = new Map<string, BuildingFloorOption>();
  for (const row of rows) {
    const building = String(row['building'] ?? '');
    const floor = String(row['floor'] ?? '');
    if (building === '' || floor === '') continue;
    const value = buildingFloorValue({ building, floor });
    if (!pairs.has(value)) {
      pairs.set(value, { building, floor, value, label: `${building}-${floor}` });
    }
  }
  return [...pairs.values()];
}
